import React from "react";
import { Link } from "react-router-dom";
import {
    ClipboardList,
    DraftingCompass,
    Building2,
    ShieldCheck,
    KeyRound,
    Hammer,
    ArrowRight,
    Monitor,
} from "lucide-react";

const STEPS = [
    {
        icon: ClipboardList,
        title: "Project Intake & Feasibility",
        timeline: "Day 1–3",
        desc: "We review your goals, property, and jurisdiction. You receive a written scope, fee proposal, and realistic permit timeline.",
    },
    {
        icon: DraftingCompass,
        title: "Design & Plan Development",
        timeline: "Week 1–4",
        desc: "Our architects draft site, floor, and elevation plans. You approve revisions before anything goes to the city.",
    },
    {
        icon: Building2,
        title: "Engineering Coordination",
        timeline: "Week 3–6",
        desc: "Structural, civil, and MEP engineers prepare stamped drawings. We assemble the complete submittal package.",
    },
    {
        icon: ShieldCheck,
        title: "Permit Submission & Corrections",
        timeline: "Week 4–10",
        desc: "We submit to the jurisdiction and handle every correction round directly with plan checkers—no back-and-forth for you.",
    },
    {
        icon: KeyRound,
        title: "Final Approval & Permit Issuance",
        timeline: "Week 8–14",
        desc: "Pay permit fees, receive stamped plans, and get cleared to build. We archive every document in your portal.",
    },
    {
        icon: Hammer,
        title: "Construction Support",
        timeline: "During Build",
        desc: "Revision permits, deferred submittals, and inspector coordination—we stay on your team until final sign-off.",
    },
];

export default function HowItWorks() {
    return (
        <div data-testid="page-how-it-works" className="bg-white">
            {/* HERO */}
            <section className="bg-[#0A2540] text-white py-24" data-testid="how-hero">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">How It Works</div>
                    <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
                        A predictable path from idea to <span className="text-[#C5A059]">approved permit.</span>
                    </h1>
                    <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
                        Transparent timelines, a real point of contact, and a client portal so you always know where your permit stands.
                    </p>
                </div>
            </section>

            {/* TIMELINE */}
            <section className="py-24" data-testid="timeline-section">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="relative">
                        {/* vertical line */}
                        <div className="absolute left-7 top-0 bottom-0 w-px bg-[#E2E8F0] hidden md:block" aria-hidden="true" />
                        <ol className="space-y-10">
                            {STEPS.map((s, i) => (
                                <li
                                    key={s.title}
                                    className="relative grid grid-cols-[auto_1fr] gap-6 md:gap-10"
                                    data-testid={`timeline-step-${i + 1}`}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="w-14 h-14 bg-[#C5A059] text-white flex items-center justify-center text-lg font-bold font-[Outfit] z-10">
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                    </div>
                                    <div className="pb-2">
                                        <div className="flex flex-wrap items-center gap-3 mb-2">
                                            <s.icon size={18} className="text-[#0A2540]" />
                                            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#475569]">
                                                {s.timeline}
                                            </span>
                                        </div>
                                        <h3 className="text-2xl font-semibold text-[#0A2540]">{s.title}</h3>
                                        <p className="mt-3 text-[#475569] leading-relaxed max-w-2xl">{s.desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* CLIENT PORTAL */}
            <section className="py-24 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="portal-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">Client Portal</div>
                        <h2 className="mt-3 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                            Real-time visibility on every permit.
                        </h2>
                        <p className="mt-5 text-[#475569] leading-relaxed">
                            Track submittals, review corrections, download stamped plans, and message your project manager—anytime, from any device. No more calling the city to find out where things stand.
                        </p>
                        <ul className="mt-8 space-y-3 text-[#0A2540]">
                            {[
                                "Live permit status & jurisdiction updates",
                                "Document repository for plans & approvals",
                                "Milestone alerts via email and SMS",
                                "Direct messaging with your project manager",
                            ].map((f) => (
                                <li key={f} className="flex items-center gap-3">
                                    <span className="w-1.5 h-1.5 bg-[#C5A059] rounded-full" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/contact"
                            className="mt-10 inline-flex items-center gap-2 btn-blue px-7 py-3.5 text-sm font-semibold"
                            data-testid="portal-cta"
                        >
                            Request Portal Access <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="relative aspect-[4/3] bg-[#0A2540] p-8 flex items-center justify-center overflow-hidden">
                        <div className="absolute inset-0 opacity-20">
                            <img
                                src="https://images.unsplash.com/photo-1754780960162-839cda44d736?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50c3xlbnwwfHx8fDE3NzcwNTQ2NzV8MA&ixlib=rb-4.1.0&q=85"
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="relative z-10 w-full max-w-sm bg-white border border-[#E2E8F0] p-6 shadow-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-[#0A2540] font-semibold text-sm">
                                    <Monitor size={16} className="text-[#C5A059]" /> Project #P-2847
                                </div>
                                <span className="text-[10px] uppercase tracking-widest text-[#C5A059] font-semibold">Live</span>
                            </div>
                            <div className="mt-4 space-y-3">
                                {[
                                    { label: "Plans Submitted", done: true },
                                    { label: "Plan Check Review", done: true },
                                    { label: "Corrections Pending", done: false, active: true },
                                    { label: "Final Approval", done: false },
                                ].map((r) => (
                                    <div key={r.label} className="flex items-center gap-3 text-sm">
                                        <div className={`w-3 h-3 rounded-full ${r.done ? "bg-[#C5A059]" : r.active ? "bg-[#0A2540] ring-4 ring-[#0A2540]/10" : "bg-[#E2E8F0]"}`} />
                                        <span className={r.done ? "text-[#475569] line-through" : "text-[#0A2540]"}>{r.label}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-5 pt-4 border-t border-[#E2E8F0] text-xs text-[#475569]">
                                Last update: 2 hours ago by Mark (PM)
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
